import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import TempoKeyChip from "@/components/tempo/TempoKeyChip";
import { analyzeTempoKey, decodeSong } from "@/lib/tempoKey";
import { saveSongTempo } from "@/lib/mixDeskApi";
import { safeApiError } from "@/lib/safeErrorText";

/** The song's tempo and key on the Mix Desk — found from the song the first time, then saved with it. */
export default function SongTempo({ song }) {
  const [tempo, setTempo] = useState(song.tempo || null);
  const [detecting, setDetecting] = useState(false);
  const timer = useRef(null);
  const started = useRef(false);
  const pending = useRef(null);
  const detect = useCallback(async () => {
    setDetecting(true);
    try {
      const t = await analyzeTempoKey(await decodeSong(song.audio_url));
      setTempo(t);
      await saveSongTempo(song.id, t);
    } catch { toast.error("Couldn't find the tempo and key", { description: "Press Fix to set them yourself." }); }
    finally { setDetecting(false); }
  }, [song.id, song.audio_url]);
  useEffect(() => { if (!started.current && !song.tempo && song.audio_url) { started.current = true; detect(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const t = pending.current;
    pending.current = null;
    if (t) saveSongTempo(song.id, t).catch((e) => toast.error("Couldn't save the tempo and key", { description: safeApiError(e) }));
  }, [song.id]);
  useEffect(() => flush, [flush]);
  const onChange = (t) => {
    setTempo(t);
    pending.current = t;
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  };
  return (
    <div data-testid="mix-tempo" className="mt-4">
      <TempoKeyChip tempo={tempo} detecting={detecting} onChange={onChange} onRedetect={song.audio_url ? detect : null} />
    </div>
  );
}
